/* eslint-disable no-underscore-dangle */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Input, Button, CardActions, Checkbox, Switch } from 'react-toolbox';
import Card from 'components/Card';
import cx from 'classnames';
import MessageBox from 'components/MessageBox';
import PhoneInput from 'components/PhoneType/PhoneInput';
import ColoredButton from 'components/ColoredButton';
import StyledSelect from 'components/StyledSelect';
import ReactSelect from 'components/StyledSelect/react_select';
import RadioButtons from 'components/RadioButtons';
import TaggedInput from 'components/TaggedInput';
import InstallerPrioritizationSliders from 'components/InstallerPrioritizationSliders';
import FlexBox from 'flexbox-react';
import { ThemeProvider } from 'react-css-themr';
import styles from 'styles/form';
import RTInput from 'styles/input';
import RTDropdown from 'styles/dropdown';
import RTCard from 'styles/card';
import StoreContactForm from './storeContactForm';
import { error, confirm, generateSource, removeNestedError } from '../../../../../utils';
import WorkroomType from 'types/workroom';
import PricePatchType from 'types/pricePatch';
import StoreType from 'types/store';
import DivisionType from 'types/division';
import MarketType from 'types/market';
import RegionType from 'types/region';
import { can } from '../../../../../services';
import { getRole, getPosition, getManageVendorWorkrooms } from '../../../../../reducers';
import RTCheckbox from 'styles/checkbox.scss';
import { STATES, TIMEZONES, EMAIL_VALIDATION_REGEX, VENDORS } from 'config/constants';

const ISO_IST = ['ISO', 'IST Wilkesboro', 'IST Indianapolis'];

const extractWeight = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'object' && val.weight !== undefined) {
    return parseInt(val.weight, 10) || 0;
  }
  return parseInt(val, 10) || 0;
};

const getSurfaceTotal = (surfaceWeights) => {
  if (!surfaceWeights || typeof surfaceWeights !== 'object') return 0;
  return Object.values(surfaceWeights).reduce((sum, val) => sum + extractWeight(val), 0);
};

const largeInput = cx(styles.inputs, styles.largeInput);
const mediumInput = cx(styles.inputs, styles.mediumInput);
const smallInput = cx(styles.inputs, styles.smallInput);
const cancelClass = cx([styles.cancelButton, styles.actionButton]);
const titleStyle = cx([styles.title, styles.divider]);
const deliveryRadioBtns = cx([styles.inputs, styles.deliveryRadioBtns]);
const theme = { RTInput, RTDropdown, RTCard };

class StoreForm extends Component {
  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleActive = this.handleChange.bind(this, 'active');
    this.handleRole = this.handleChange.bind(this, 'role');
    this.handleCancel = this.handleCancel.bind(this);
    this.statesSource = STATES.map(state => ({ value: state, label: state }));
    this.expressShipDaysSource = [
      { value: 0, label: '0' },
      { value: 10, label: '10' },
      { value: 14, label: '14' },
    ];
    this.handleState = this.handleChange.bind(this, 'state');
    this.handleExpressShipDays = this.handleChange.bind(this, 'express_ship_days');
    this.timezonesSource = TIMEZONES.map(tz => ({ value: tz, label: tz }));
    this.handleTimezone = this.handleChange.bind(this, 'timezone');
    this.handleWorkroom = this.handleChange.bind(this, 'workroom_id');
    this.workroomsSource = generateSource(this.props.workrooms);
    this.handleMarket = this.handleChange.bind(this, 'market_id');
    this.handleRegion = this.handleRegion.bind(this, 'region_id');
    this.handleDivision = this.handleDivision.bind(this, 'division_id');
    this.handleIsoIst = this.handleChange.bind(this, 'cpo_iso');
    this.handlePricePatch = this.handleChange.bind(this, 'price_patch');
    this.pricePatchesSource = generateSource(this.props.pricePatches);
    this.onNameChange = this.handleChange.bind(this, 'name');
    this.onNumberChange = this.handleChange.bind(this, 'number');
    this.onAddressChange = this.handleChange.bind(this, 'address');
    this.onCityChange = this.handleChange.bind(this, 'city');
    this.onZipChange = this.handleChange.bind(this, 'zip_code');
    this.onPhoneChange = this.handleChange.bind(this, 'phone_number');
    this.onQuickbooksChange = this.handleChange.bind(this, 'quickbooks_ident');
    this.onFaxFlooringChange = this.handleChange.bind(this, 'fax_number_flooring');
    this.onFaxSalesChange = this.handleChange.bind(this, 'fax_number_installed_sales');
    this.onFaxReceivingChange = this.handleChange.bind(this, 'fax_number_receiving');
    this.handleDisableIstEmails = this.handleChange.bind(this, 'disable_cpo_emails');
    this.handleDeliveryType = this.handleChange.bind(this, 'delivery_method');
    this.handleCoveredZipChange = this.handleChange.bind(this, 'covered_zip_codes');
    this.handleVendor = this.handleChange.bind(this, 'vendor');
    this.vendorsSource = VENDORS.map(v => ({ value: v, label: v }));
    this.addStoreContact = this.addStoreContact.bind(this);
    this.confirmAndDelete = this.confirmAndDelete.bind(this);
    this.handleCcsSchedulable = this.handleChange.bind(this, 'ccs_schedulable');
    this.handlePrioritizationWeightsChange = this.handlePrioritizationWeightsChange.bind(this);
    const { store_contacts, ...store } = props.storeDetails || { price_patch: '', disable_cpo_emails: true };
    this.state = { store, store_contacts: (store_contacts || []), emailValidations: [] };
  }

  getWeightsValidationMessage = () => {
    const { store } = this.state;
    const weights = store.installer_prioritization_weights || {};

    const invalidSurfaces = Object.entries(weights)
      .filter(([, surfaceWeights]) => {
        const total = getSurfaceTotal(surfaceWeights);
        return total > 0 && total !== 100;
      })
      .map(([surfaceType]) => surfaceType);

    if (invalidSurfaces.length === 0) return null;
    return `Installer weights must total 100% for: ${invalidSurfaces.join(', ')}`;
  }

  handlePrioritizationWeightsChange = (weights) => {
    const store = { ...this.state.store };
    store.installer_prioritization_weights = weights;
    this.setState({ store });
  }

  isInstallerWeightsInvalid = () => {
    const { store } = this.state;
    const weights = store.installer_prioritization_weights || {};

    return Object.values(weights).some((surfaceWeights) => {
      const total = getSurfaceTotal(surfaceWeights);
      return total > 0 && total !== 100;
    });
  }

  confirmAndDelete = (index) => {
    const sc = { ...this.state.store_contacts[index] };
    const name = (sc.name && sc.name.trim()) || 'the store contact';
    const message = `Are you sure you want to delete ${name}?`;
    confirm(message).then(() => {
      this.deleteStoreContact(index);
    });
  }

  deleteStoreContact = (index) => {
    const storeContacts = this.state.store_contacts;
    const storeContact = storeContacts[index];
    if (storeContact.id !== undefined) {
      storeContact._destroy = true;
      this.setState({ store_contacts: storeContacts });
    } else {
      this.setState({
        store_contacts: storeContacts
          .slice(0, index)
          .concat(storeContacts.slice(index + 1)),
      });
    }
    this.props.onRemoveNestedErrors(
      removeNestedError('store_contacts', index, this.props.errors),
    );
  }

  addStoreContact = () => {
    const storeContacts = this.state.store_contacts;
    this.setState({ store_contacts: storeContacts.concat({
      assessment_updates: true,
      assessment_refigure: true,
      installation_updates: true,
    }) });
  }

  handleDivision = (key, value) => {
    this.props.onDivisionChange(value);
    this.handleChange(key, value);
    this.removeAttributeName('region');
    this.removeAttributeName('market');
  }

  removeAttributeName = (attr) => {
    this.handleChange(`${attr}_id`, null);
    this.handleChange(`${attr}_name`, null);
  }

  handleRegion = (key, value) => {
    this.props.onRegionChange(value);
    this.handleChange(key, value);
    this.removeAttributeName('market');
  }

  handleChange = (key, value) => {
    const store = this.state.store;
    store[key] = value;
    this.setState({ store });
  }

  handleSCChange = (i, key, value) => {
    const storeContacts = this.state.store_contacts;
    if (key === 'email') {
      const strippedVal = value.replaceAll(' ', '');
      storeContacts[i][key] = strippedVal;
      const regex = new RegExp(EMAIL_VALIDATION_REGEX);
      if (regex.test(strippedVal)) storeContacts[i].emailError = false;
      else storeContacts[i].emailError = true;
    } else storeContacts[i][key] = value;
    this.setState({ store_contacts: storeContacts });
  }

  handleCancel = () => this.props.onCancel();

  handleSave = () => {
    const { store_contacts, store: { price_patch, ...store } } = this.state;
    const data = {
      ...store,
      price_patch_id: price_patch,
      store_contacts_attributes: store_contacts,
    };
    this.props.onSave(data);
  }

  generateSource = (array, defaultValue = null) => {
    if (defaultValue !== null && array.length === 0 && !!this.state.store.id) {
      array.push(defaultValue);
    }
    return generateSource(array);
  }

  validateStoreNumber = () => {
    const { store } = this.state;
    const number = store.number;
    const vendor = store.vendor || 'LHI';
    if (number == null || number === '') return 'Store number is required.';
    if (vendor == null || vendor === '') return 'Vendor is required.';
    switch (vendor) {
      case 'LHI':
      case 'IFI':
        if (!/^[0-9]{4}$/.test(number)) return 'LHI/IFI store number must be exactly 4 digits.';
        break;
      case 'RF':
        if (!/^RF[0-9]{4}$/.test(number)) return "RF store number must be 'RF' followed by 4 digits.";
        if (number.length !== 6) return 'RF store number must be exactly 6 characters.';
        break;
      case 'FD':
        if (!/^FD[0-9]{4}$/.test(number)) return "FD store number must be 'FD' followed by 4 digits.";
        if (number.length !== 6) return 'FD store number must be exactly 6 characters.';
        break;
      case 'AH':
        if (!/^AH[0-9]{5}$/.test(number)) return "AH store number must be 'AH' followed by 5 digits.";
        if (number.length !== 7) return 'AH store number must be exactly 7 characters.';
        break;
      case 'BJ':
        if (!/^BJ[0-9]{3}$/.test(number)) return "BJ store number must be 'BJ' followed by 3 digits.";
        if (number.length !== 5) return 'BJ store number must be exactly 5 characters.';
        break;
      case 'SAM':
        if (!/^SAM[0-9]{4}$/.test(number)) return "SAM store number must be 'SAM' followed by 4 digits.";
        if (number.length !== 7) return 'SAM store number must be exactly 7 characters.';
        break;
      case 'WAY':
        if (!/^WAY[0-9]{3}$/.test(number)) return "WAY store number must be 'WAY' followed by 3 digits.";
        if (number.length !== 6) return 'WAY store number must be exactly 6 characters.';
        break;
      default:
        return 'Unknown vendor.';
    }
    return null;
  };

  renderTitle = (store, isEdit) => {
    const switchClassNames = cx({
      [styles.switchTitleAdd]: !isEdit,
      [styles.switchTitle]: true,
    });
    return (
      <FlexBox justifyContent="space-between" className={styles.divider}>
        <div className={titleStyle}>
          <h5 className={styles.cardTitle}>{isEdit ? 'Edit Store' : 'Add Store'}</h5>
          <p className={styles.cardSubtitle}>
        Please ensure that all fields contain accurate information
          </p>
        </div>
        <div style={{ paddingTop: '24px' }}>
          <Switch
            name="ccs_schedulable"
            label="CCS Schedulable"
            onChange={this.handleCcsSchedulable}
            className={switchClassNames}
            checked={store.ccs_schedulable}
          />
        </div>
      </FlexBox>);
  };

  renderPrioritization = (store, canEditDetails) => {
    const { installersForPrioritization, ifiSurfaceTypes } = this.props;
    return (
      <div className={styles.prioritizationSection}>
        <h5 className={styles.childTitle}>Installer Prioritization Weights</h5>
        <InstallerPrioritizationSliders
          weights={store.installer_prioritization_weights || {}}
          installers={installersForPrioritization || []}
          surfaceTypes={ifiSurfaceTypes || []}
          onChange={this.handlePrioritizationWeightsChange}
          disabled={!canEditDetails}
        />
      </div>
    );
  };

  render = () => {
    const { store, store_contacts = [] } = this.state;
    const { errors, divisions, regions, markets, role, storeDetails,
      position, manageVendorWorkrooms } = this.props;
    const { vendor } = store;
    const isEdit = !!store.id;
    const gError = error('global', errors);
    const canEditDetails = can(role, 'update', 'store_details');
    const canEditContact = can(role, 'update', 'store_contact');
    const canAddContact = can(role, 'create', 'store_contact');
    const canManageVendors = can(role, 'manage', 'manage_vendors');
    const contactError = store_contacts.length && store_contacts.some(c =>
      'emailError' in c && c.emailError);
    const storeNumberError = this.validateStoreNumber();
    const isIfiVendorStore = storeDetails && storeDetails.vendor === 'IFI';
    const canManagePrioritizationWeights = can(role, 'manage', 'installer_prioritization_weights', {
      position: position || '',
      manageVendorWorkrooms: manageVendorWorkrooms || {},
    });
    const showPrioritizationWeights = isIfiVendorStore && canManagePrioritizationWeights;
    const weightsInvalid = this.isInstallerWeightsInvalid();
    const weightsValidationMessage = showPrioritizationWeights
      ? this.getWeightsValidationMessage()
      : null;
    return (<ThemeProvider theme={theme}>
      <Card>
        {this.renderTitle(store, isEdit)}
        {gError ? <MessageBox error message={gError} /> : null}
        {storeNumberError && <MessageBox error message={storeNumberError} />}
        {canEditDetails && <div>
          <FlexBox flexWrap="wrap" className={styles.divider}>
            {canManageVendors && <StyledSelect
              className={smallInput}
              label="Vendor"
              placeholder={'Select Vendor'}
              onChange={this.handleVendor}
              options={this.vendorsSource}
              value={store.vendor}
              error={error('vendor', errors)}
              required
            />}
            <Input
              label="Store Name"
              className={mediumInput}
              value={store.name}
              onChange={this.onNameChange}
              error={error('name', errors)}
              required
            />
            <Input
              label="Store Number"
              className={smallInput}
              value={store.number}
              onChange={this.onNumberChange}
              error={error('number', errors)}
              required
            />
            <Input
              label="Street Address"
              className={largeInput}
              value={store.address}
              onChange={this.onAddressChange}
              error={error('address', errors)}
              required
            />
            <Input
              label="City"
              className={largeInput}
              value={store.city}
              onChange={this.onCityChange}
              error={error('city', errors)}
              required
            />
            <StyledSelect
              className={mediumInput}
              label="State"
              onChange={this.handleState}
              options={this.statesSource}
              value={store.state}
              placeholder={'Select State'}
              error={error('state', errors)}
              required
            />
            <Input
              label="Zip Code"
              className={smallInput}
              value={store.zip_code}
              onChange={this.onZipChange}
              error={error('zip_code', errors)}
              required
            />
            <PhoneInput
              label="Phone Number"
              type="tel"
              value={store.phone_number}
              className={largeInput}
              onChange={this.onPhoneChange}
              error={error('phone_number', errors)}
              required
            />
            <StyledSelect
              className={largeInput}
              label="Time Zone"
              placeholder={'Select Time Zone'}
              onChange={this.handleTimezone}
              options={this.timezonesSource}
              value={store.timezone}
              error={error('timezone', errors)}
              required
            />
            <ReactSelect
              className={largeInput}
              label="Price Patch Number"
              placeholder={'Select Price Patch'}
              onChange={this.handlePricePatch}
              options={this.pricePatchesSource}
              value={store.price_patch || ''}
              error={error('price_patch', errors)}
              simpleValue
              required
            />
            <Input
              label="Quickbooks ID"
              className={largeInput}
              value={store.quickbooks_ident}
              onChange={this.onQuickbooksChange}
              error={error('quickbooks_ident', errors)}
              required
            />
            <StyledSelect
              className={largeInput}
              label="ISO/IST"
              placeholder={'Select ISO/IST'}
              onChange={this.handleIsoIst}
              options={ISO_IST.map(istIso => ({ value: istIso, label: istIso }))}
              value={store.cpo_iso}
              error={error('cpo_iso', errors)}
              required
            />
            <PhoneInput
              label="Fax Number: Flooring"
              type="tel"
              value={store.fax_number_flooring}
              className={largeInput}
              onChange={this.onFaxFlooringChange}
              error={error('fax_number_flooring', errors)}
            />
            <PhoneInput
              label="Fax Number: Installed Sales"
              type="tel"
              value={store.fax_number_installed_sales}
              className={largeInput}
              onChange={this.onFaxSalesChange}
              error={error('fax_number_installed_sales', errors)}
            />
            <PhoneInput
              label="Fax Number: Receiving"
              type="tel"
              value={store.fax_number_receiving}
              className={largeInput}
              onChange={this.onFaxReceivingChange}
              error={error('fax_number_receiving', errors)}
            />
            <StyledSelect
              className={mediumInput}
              label="Express Ship Days"
              onChange={this.handleExpressShipDays}
              options={this.expressShipDaysSource}
              value={store.express_ship_days}
              placeholder={'Select Express Ship Days'}
              error={error('express_ship_days', errors)}
              required
            />
          </FlexBox>
          <FlexBox flexWrap="wrap" className={styles.divider}>
            <StyledSelect
              className={largeInput}
              label={vendor === 'IFI' ? 'District' : 'Workroom'}
              placeholder={vendor === 'IFI' ? 'Select District' : 'Select Workroom'}
              onChange={this.handleWorkroom}
              options={this.workroomsSource}
              value={store.workroom_id}
              error={error('workroom_id', errors)}
              required
            />
            <StyledSelect
              className={largeInput}
              label="Division"
              placeholder={'Select Division'}
              onChange={this.handleDivision}
              options={generateSource(divisions)}
              value={store.division_id}
              error={error('division_id', errors)}
              required
            />
            <StyledSelect
              className={largeInput}
              label="Region"
              placeholder={'Select Region'}
              onChange={this.handleRegion}
              options={
                this.generateSource(regions, { id: store.region_id, name: store.region_name })
              }
              value={store.region_id}
              error={error('region_id', errors)}
              required
            />
            <StyledSelect
              className={largeInput}
              label="Market"
              placeholder={'Select Market'}
              onChange={this.handleMarket}
              options={
                this.generateSource(markets, { id: store.market_id, name: store.market_name })
              }
              value={store.market_id}
              error={error('market_id', errors)}
              required
            />
            <br />
            <div className={largeInput}>
              <TaggedInput
                label="Covered Zip Codes"
                values={(store.covered_zip_codes || []).sort((a, b) => a - b) || []}
                onChange={this.handleCoveredZipChange}
                errors={errors.covered_zip_codes || []}
              />
            </div>
            <RadioButtons
              className={deliveryRadioBtns}
              label="Delivery Method"
              onChange={this.handleDeliveryType}
              selectedValue={store.delivery_method}
              radioButtons={[{ label: 'None', value: 'None' }, { label: 'Delivery', value: 'Delivery' }, { label: 'Pickup', value: 'Pickup' }]}
              required
            />
          </FlexBox>
          <FlexBox flexWrap="wrap" className={styles.divider}>
            <Checkbox
              checked={store.disable_cpo_emails}
              label="Disable IST Emails"
              onChange={this.handleDisableIstEmails}
              className={styles.checkboxListItem}
              theme={RTCheckbox}
            />
          </FlexBox>
        </div>}
        {canEditContact && <FlexBox flexWrap="wrap" className={styles.divider}>
          <FlexBox justifyContent="space-between" flex="1">
            <h5 className={styles.childTitle}>Store Contacts</h5>
            {canAddContact && <Button
              className={styles.addChildButton}
              onClick={this.addStoreContact}
              label="Add Store Contact"
            />}
          </FlexBox>
          {store_contacts.map((contact, i) => ((!contact._destroy) ?
            /* eslint-disable react/jsx-no-bind */
            (<StoreContactForm
              key={contact.id}
              storeContact={contact}
              errors={errors}
              onChange={this.handleSCChange.bind(this, i)}
              index={i}
              onDelete={this.confirmAndDelete}
              role={role}
            />) : null),
            /* eslint-enable react/jsx-no-bind */
          )}
        </FlexBox>}
        {showPrioritizationWeights && this.renderPrioritization(store, canEditDetails)}
        <FlexBox justifyContent="flex-end" flexWrap="wrap" alignItems="center">
          <CardActions className={styles.actionsWrapper}>
            {weightsValidationMessage && (
              <span className={styles.weightsError}>{weightsValidationMessage}</span>
            )}
            <ColoredButton
              className={cancelClass}
              responsive
              color="gray"
              label="Cancel"
              onClick={this.handleCancel}
            />
            <ColoredButton
              className={styles.actionButton}
              responsive
              color="green"
              label={isEdit ? 'Save Changes' : 'Add store'}
              onClick={this.handleSave}
              disabled={contactError || !!storeNumberError || weightsInvalid}
            />
          </CardActions>
        </FlexBox>
      </Card>
    </ThemeProvider>);
  }
}

StoreForm.propTypes = {
  dispatch: PropTypes.func,
  workrooms: PropTypes.arrayOf(WorkroomType).isRequired,
  pricePatches: PropTypes.arrayOf(PricePatchType).isRequired,
  divisions: PropTypes.arrayOf(DivisionType).isRequired,
  regions: PropTypes.arrayOf(RegionType).isRequired,
  markets: PropTypes.arrayOf(MarketType).isRequired,
  storeDetails: StoreType,
  onRemoveNestedErrors: PropTypes.func,
  onDivisionChange: PropTypes.func,
  onRegionChange: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  errors: PropTypes.object,
  role: PropTypes.string.isRequired,
  position: PropTypes.string,
  manageVendorWorkrooms: PropTypes.object,
  installersForPrioritization: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    phase_count: PropTypes.number.isRequired,
  })),
  ifiSurfaceTypes: PropTypes.arrayOf(PropTypes.string),
};

export default connect(state => ({
  role: getRole(state),
  position: getPosition(state),
  manageVendorWorkrooms: getManageVendorWorkrooms(state),
}))(StoreForm);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/stores/components/storeForm.jsx